import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-5702">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-5703">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid */}
        <div className="flex items-center gap-1.5" data-id="element-5704">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-5705">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-white text-slate-600 border border-slate-200'}`} data-id="element-5706">
                {letter}
              </div>;
        })}
        </div>
        {/* Compact result badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-bold text-sm
          ${isInKofschip ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-orange-50 border-orange-300 text-orange-800'}`} data-id="element-5707">
          <span className="font-black text-base" data-id="element-5708">{stem}</span>
          <span className="opacity-60" data-id="element-5709">+</span>
          <span className="font-black text-base" data-id="element-5710">{useTE ? '-te' : '-de'}</span>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5711">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-5712">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5713">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-5714">
            <span className="text-2xl" data-id="element-5715">⛵</span>
            <div className="text-left" data-id="element-5716">
              <div className="text-sm font-bold text-blue-900" data-id="element-5717">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-5718">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Verb → Stem */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5719">
          <div className="flex items-start gap-3" data-id="element-5720">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5721">
              <span className="text-blue-600 font-bold text-sm" data-id="element-5722">1</span>
            </div>
            <div className="flex-1" data-id="element-5723">
              <h4 className="text-sm font-bold text-blue-900 mb-3" data-id="element-5724">
                Stap 1: Vind de stam
              </h4>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-5725">
                <div className="text-center" data-id="element-5726">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-5727">
                    Werkwoord
                  </div>
                  <div className="text-xl font-black text-blue-700 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200" data-id="element-5728">
                    {verb}
                  </div>
                </div>
                <div className="text-2xl text-slate-300" data-id="element-5729">→</div>
                <div className="text-center" data-id="element-5730">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-5731">
                    Stam (-en weg)
                  </div>
                  <div className="text-xl font-black text-purple-700 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200" data-id="element-5732">
                    {stem}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Last Letter Check */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-cyan-100 shadow-sm" data-id="element-5733">
          <div className="flex items-start gap-3" data-id="element-5734">
            <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0" data-id="element-5735">
              <span className="text-cyan-600 font-bold text-sm" data-id="element-5736">2</span>
            </div>
            <div className="flex-1" data-id="element-5737">
              <h4 className="text-sm font-bold text-cyan-900 mb-3" data-id="element-5738">
                Stap 2: Kijk naar de laatste letter van de stam
              </h4>
              <div className="text-center" data-id="element-5739">
                <div className="inline-flex items-center gap-2 bg-cyan-50 px-6 py-3 rounded-xl border-2 border-cyan-200" data-id="element-5740">
                  <span className="text-sm text-slate-600 font-semibold" data-id="element-5741">
                    Laatste letter:
                  </span>
                  <span className="text-3xl font-black text-cyan-700" data-id="element-5742">
                    {lastLetter.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: KOFSCHIP Check */}
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200 shadow-md" data-id="element-5743">
          <div className="flex items-start gap-3 mb-4" data-id="element-5744">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-5745">
              <span className="text-indigo-600 font-bold text-sm" data-id="element-5746">3</span>
            </div>
            <div data-id="element-5747">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-5748">
                Stap 3: Staat de letter in 't KOFSCHIP?
              </h4>
              <p className="text-xs text-slate-600 italic" data-id="element-5749">
                't KOFSCHIP is een ezelsbruggetje om te onthouden welke letters
                -te krijgen
              </p>
            </div>
          </div>
          <div className="flex justify-center gap-2 mb-4" data-id="element-5750">
            <div className="text-2xl font-black text-slate-400 mr-2" data-id="element-5751">'t</div>
            {kofschipLetters.map((letter, index) => {
            const isMatch = letter === lastLetter.toUpperCase();
            return <motion.div key={letter} initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: index * 0.05,
              type: 'spring',
              stiffness: 300
            }} className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-2xl transition-all duration-300 shadow-md
                    ${isMatch ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white ring-4 ring-green-200 scale-110' : 'bg-white text-slate-700 border-2 border-slate-200'}`} data-id="element-5752">
                  {letter}
                </motion.div>;
          })}
          </div>
          <div className={`text-center p-4 rounded-xl border-2 font-bold ${isInKofschip ? 'bg-green-50 border-green-200 text-green-800' : 'bg-orange-50 border-orange-200 text-orange-800'}`} data-id="element-5753">
            {isInKofschip ? <div data-id="element-5754">
                <div className="text-lg mb-1" data-id="element-5755">
                  ✅ Ja! De letter{' '}
                  <span className="text-2xl" data-id="element-5756">{lastLetter.toUpperCase()}</span>{' '}
                  zit in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-5757">
                  Dus: verleden tijd krijgt <strong data-id="element-5758">-te</strong> (of{' '}
                  <strong data-id="element-5759">-tte</strong> bij meervoud)
                </div>
              </div> : <div data-id="element-5760">
                <div className="text-lg mb-1" data-id="element-5761">
                  ❌ Nee! De letter{' '}
                  <span className="text-2xl" data-id="element-5762">{lastLetter.toUpperCase()}</span>{' '}
                  zit NIET in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-5763">
                  Dus: verleden tijd krijgt <strong data-id="element-5764">-de</strong> (of{' '}
                  <strong data-id="element-5765">-dde</strong> bij meervoud)
                </div>
              </div>}
          </div>
        </div>

        <div className="space-y-4" data-id="element-5766">
          <div className="bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-5767">
            <div className="flex items-start gap-3" data-id="element-5768">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-5769">
                <span className="text-emerald-600 text-lg" data-id="element-5770">📝</span>
              </div>
              <div data-id="element-5771">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5772">
                  Voorbeeld met dit werkwoord
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5773">
                  <p data-id="element-5774">
                    <strong data-id="element-5775">Stam:</strong> {stem}
                  </p>
                  <p data-id="element-5776">
                    <strong data-id="element-5777">Laatste letter:</strong> {lastLetter.toUpperCase()}
                    {isInKofschip ? ' (zit in KOFSCHIP ✓)' : ' (zit NIET in KOFSCHIP ✗)'}
                  </p>
                  <p data-id="element-5778">
                    <strong data-id="element-5779">Verleden tijd:</strong>{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-5780">
                      {stem}
                      {useTE ? 'te' : 'de'}
                    </span>{' '}
                    (enkelvoud) /{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-5781">
                      {stem}
                      {useTE ? 'tten' : 'dden'}
                    </span>{' '}
                    (meervoud)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-5782">
            <div className="flex items-start gap-3" data-id="element-5783">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-5784">
                <span className="text-amber-600 text-lg" data-id="element-5785">💡</span>
              </div>
              <div data-id="element-5786">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-5787">
                  Ezelsbruggetje
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5788">
                  Onthoud: <strong data-id="element-5789">'t KOFSCHIP</strong> vaart door de haven. Als
                  de laatste letter van de stam in dit woord zit, gebruik je{' '}
                  <strong data-id="element-5790">-te</strong>. Anders gebruik je <strong data-id="element-5791">-de</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}